import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import constants from '../libs/constant';

const SHARE_REDIRECT_ENDPOINTS = {
  group: '/mobile/group/share-group-redirect-url',
  community: '/mobile/community/share-community-redirect-url',
  activity: '/mobile/activity/share-activity-redirect-url',
  profile: '/mobile/dashboard/share-app-redirect-url',
  resetPassword: '/mobile/auth/password-reset-redirect',
  freelancer: '/mobile/freelancer/share-freelancer-redirect-url',
  // WEAL-461 - backend route is GET /api/v1/mobile/referral/share-redirect (public, no auth),
  // registered in weall-web-backend/routes/v1/mobile/referral.route.js.
  referral: '/mobile/referral/share-redirect',
};

const extractVar = (html, varName) => {
  const match = html.match(new RegExp('var ' + varName + '\\s*=\\s*"([^"]*)"'));
  return match ? match[1] : '';
};

const ShareRedirect = ({ shareType }) => {
  const [searchParams] = useSearchParams();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError(constants.shareRedirect.errors.tokenRequired);
      setLoading(false);
      return;
    }

    const endpoint = SHARE_REDIRECT_ENDPOINTS[shareType];

    if (!endpoint) {
      setError(constants.shareRedirect.errors.loadFailed);
      setLoading(false);
      return;
    }

    const fetchContent = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:5558'}${process.env.REACT_APP_API_PATH || '/api'}${process.env.REACT_APP_API_VERSION || '/v1'}${endpoint}?token=${encodeURIComponent(token)}`;

        const response = await axios.get(apiUrl, {
          headers: {
            Accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
          },
          responseType: 'text',
        });

        setContent(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching share page:', err);
        setError(constants.shareRedirect.errors.loadFailed);
        setLoading(false);
      }
    };

    fetchContent();
  }, [searchParams, shareType]);

  // dangerouslySetInnerHTML never executes <script> tags, so run deep-link
  // logic here once the HTML content has been rendered.
  useEffect(() => {
    if (!content) return;

    const deepLink = extractVar(content, 'deepLink');
    const playStore = extractVar(content, 'playStore');
    const appStore = extractVar(content, 'appStore');

    if (!deepLink || deepLink.indexOf('{{') !== -1) return;

    const ua = navigator.userAgent || '';
    const isIOS = /iphone|ipad|ipod/i.test(ua);
    const isAndroid = /android/i.test(ua);

    const storeUrl = isIOS ? appStore : playStore;
    let appOpened = false;

    const onHide = () => { appOpened = true; };

    const onVisChange = () => {
      if (document.hidden || document.visibilityState === 'hidden') onHide();
    };

    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('blur', onHide);

    const fallback = setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('blur', onHide);
      // Only redirect to store on mobile when app is not installed
      if (!appOpened && storeUrl && (isAndroid || isIOS)) window.location.replace(storeUrl);
    }, 1500);

    window.location.href = deepLink;

    return () => {
      clearTimeout(fallback);
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('blur', onHide);
    };
  }, [content]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#eff5f1',
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#eff5f1',
          color: '#333',
          textAlign: 'center',
          padding: '20px',
        }}
      >
        <div>
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#eff5f1', minHeight: '100vh' }}>
      <div dangerouslySetInnerHTML={{ __html: content }} style={{ minHeight: '100vh' }} />
    </div>
  );
};

export default ShareRedirect;
